import { invariantResponse } from '@epic-web/invariant'
import { Img } from 'openimg/react'
import { useEffect, useState } from 'react'
import {
	type LoaderFunctionArgs,
	Form,
	Link,
	useLoaderData,
} from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { useSyncFacts } from '#app/utils/facts.client'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'
import { type Route } from './+types/$username.ts'

export async function loader({ params }: LoaderFunctionArgs) {
	const user = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			createdAt: true,
			image: { select: { id: true, objectKey: true } },
		},
		where: {
			username: params.username,
		},
	})

	invariantResponse(user, 'User not found', { status: 404 })

	return { user, userJoinedDisplay: user.createdAt.toLocaleDateString() }
}

// Client-side only component that handles FACTS sync
// Moved the useSyncFacts hook outside of the conditional rendering
function FactsSyncButton() {
	const [isMounted, setIsMounted] = useState(false)
	const syncFactsHook = useSyncFacts()
	const { syncFacts, isLoading } = syncFactsHook

	// Only run on client side
	useEffect(() => {
		setIsMounted(true)
	}, [])

	// If not mounted (server-side), render a placeholder button
	if (!isMounted) {
		return (
			<Button type="button" variant="link" size="icon" disabled={true}>
				<Icon name="update" className="scale-125 max-md:scale-150">
					Sync FACTS
				</Icon>
			</Button>
		)
	}

	return (
		<Button
			type="button"
			variant="link"
			size="icon"
			onClick={() => syncFacts()}
			disabled={isLoading}
		>
			<Icon
				name="update"
				className={`scale-125 max-md:scale-150 ${isLoading ? 'animate-spin' : ''}`}
			>
				Sync FACTS
			</Icon>
		</Button>
	)
}

export default function ProfileRoute() {
	const data = useLoaderData<typeof loader>()
	const user = data.user
	const userDisplayName = user.name ?? user.username
	const loggedInUser = useOptionalUser()
	const isLoggedInUser = user.id === loggedInUser?.id

	return (
		<div className="container mb-48 mt-36 flex flex-col items-center justify-center">
			<Spacer size="4xs" />

			<div className="container flex flex-col items-center rounded-3xl bg-muted p-12">
				<div className="relative w-52">
					<div className="absolute -top-40">
						<div className="relative">
							<Img
								src={getUserImgSrc(data.user.image?.objectKey)}
								alt={userDisplayName}
								className="h-52 w-52 rounded-full object-cover"
								width={832}
								height={832}
							/>
						</div>
					</div>
				</div>

				<Spacer size="sm" />

				<div className="flex flex-col items-center">
					<div className="flex flex-wrap items-center justify-center gap-4">
						<h1 className="text-center text-h2">{userDisplayName}</h1>
					</div>
					<p className="mt-2 text-center text-muted-foreground">
						Joined {data.userJoinedDisplay}
					</p>
					{isLoggedInUser ? (
						<div className="mt-3 flex flex-row items-center gap-x-20">
							<Form
								action="/logout"
								method="POST"
								className="flex flex-row items-center gap-x-10"
							>
								<Button type="submit" variant="link" size="icon">
									<Icon name="exit" className="scale-125 max-md:scale-150">
										Logout
									</Icon>
								</Button>
							</Form>
							{/* Use the client-only component instead */}
							<FactsSyncButton />
						</div>
					) : null}
					<div className="mt-10 flex gap-4">
						{isLoggedInUser ? (
							<>
								<Button asChild>
									<Link to="notes" prefetch="intent">
										My notes
									</Link>
								</Button>
								<Button asChild>
									<Link to="/settings/profile" prefetch="intent">
										Edit profile
									</Link>
								</Button>
							</>
						) : (
							<Button asChild>
								<Link to="notes" prefetch="intent">
									{userDisplayName}'s notes
								</Link>
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

export const meta: Route.MetaFunction = ({ data, params }) => {
	const displayName = data?.user.name ?? params.username
	return [
		{ title: `${displayName} | Epic Notes` },
		{
			name: 'description',
			content: `Profile of ${displayName} on Epic Notes`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No user with the username "{params.username}" exists</p>
				),
			}}
		/>
	)
}
